import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Async thunks for order operations
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token || localStorage.getItem('token');
      
      console.log('Auth state:', auth);
      console.log('Token from Redux:', auth.token);
      console.log('Token from localStorage:', localStorage.getItem('token'));
      console.log('Is authenticated:', auth.isAuthenticated);
      console.log('Order data customerId:', orderData.customerId);
      
      // Check if this is a guest order
      const isGuestOrder = orderData.customerId === 'guest' || !auth.isAuthenticated;
      
      if (!isGuestOrder && !token) {
        throw new Error('No authentication token found');
      }

      // The orderData from CheckoutPage.js already matches the backend Order model structure
      // Just ensure paymentDetails has the correct structure for backend
      const totalAmount = orderData.totalAmount || 0;
      const paymentStatus = orderData.paymentStatus || orderData.paymentDetails?.status || 'PENDING';
      const isPaidPayment = paymentStatus === 'PAID';
      
      const processedOrderData = {
        ...orderData,
        paymentStatus: paymentStatus,
        paymentDetails: {
          paymentMethod: orderData.paymentDetails?.paymentMethod?.toUpperCase() || 'CASH_ON_DELIVERY',
          amount: totalAmount,
          paidAmount: isPaidPayment ? totalAmount : 0,
          pendingAmount: isPaidPayment ? 0 : totalAmount,
          refundedAmount: 0,
          status: paymentStatus,
          currency: 'INR',
          description: `Payment for order via ${orderData.paymentDetails?.paymentMethod?.toUpperCase() || 'CASH_ON_DELIVERY'}`,
          // Include Razorpay details if available
          ...(orderData.paymentDetails?.razorpayOrderId && {
            razorpayOrderId: orderData.paymentDetails.razorpayOrderId,
            razorpayPaymentId: orderData.paymentDetails.razorpayPaymentId,
            razorpaySignature: orderData.paymentDetails.razorpaySignature,
            transactionId: orderData.paymentDetails.transactionId,
            gatewayTransactionId: orderData.paymentDetails.gatewayTransactionId
          })
        }
      };

      console.log('Order data being sent to backend:', processedOrderData);

      // Prepare headers - include authorization only for authenticated users
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (!isGuestOrder && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use different endpoint for guest orders
      const endpoint = isGuestOrder ? `${API_BASE_URL}/orders/guest` : `${API_BASE_URL}/orders`;
      
      const response = await axios.post(endpoint, processedOrderData, { headers });
      
      console.log('Order creation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Order creation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create order';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchUserOrders = createAsyncThunk(
  'orders/fetchUserOrders',
  async ({ userId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orders/user/${userId}?page=${page}&size=${size}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch orders'
      );
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/${orderId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch order details'
      );
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ orderId, status, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/orders/${orderId}/status`, {
        status,
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update order status'
      );
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/cancel`, {
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to cancel order'
      );
    }
  }
);

export const trackOrder = createAsyncThunk(
  'orders/trackOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/${orderId}/tracking`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch tracking information'
      );
    }
  }
);

export const processPayment = createAsyncThunk(
  'orders/processPayment',
  async ({ orderId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orders/${orderId}/payment`,
        paymentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Payment processing failed'
      );
    }
  }
);

export const requestRefund = createAsyncThunk(
  'orders/requestRefund',
  async ({ orderId, refundData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orders/${orderId}/refund`,
        refundData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to request refund'
      );
    }
  }
);

export const shipOrder = createAsyncThunk(
  'orders/shipOrder',
  async ({ orderId, trackingNumber, carrier }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/ship`, {
        trackingNumber,
        carrier,
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.order;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to ship order'
      );
    }
  }
);

export const deliverOrder = createAsyncThunk(
  'orders/deliverOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/deliver`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.order;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to deliver order'
      );
    }
  }
);

export const addOrderNote = createAsyncThunk(
  'orders/addOrderNote',
  async ({ orderId, note }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/notes`, {
        note,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add order note'
      );
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState: {
    // Order lists
    orders: [],
    
    // Current order
    currentOrder: null,
    
    // Order tracking
    trackingInfo: null,
    
    // Pagination
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    
    // Loading states
    loading: false,
    createLoading: false,
    trackingLoading: false,
    paymentLoading: false,
    
    // Error states
    error: null,
    
    // Order creation flow
    orderCreationStep: 0, // 0: Review, 1: Payment, 2: Confirmation
    createdOrderId: null,
    
    // Filters
    filters: {
      status: '',
      dateRange: null,
      sortBy: 'createdAt',
      sortDir: 'desc',
    },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
    },
    
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    
    setOrderCreationStep: (state, action) => {
      state.orderCreationStep = action.payload;
    },
    
    nextOrderCreationStep: (state) => {
      if (state.orderCreationStep < 2) {
        state.orderCreationStep += 1;
      }
    },
    
    previousOrderCreationStep: (state) => {
      if (state.orderCreationStep > 0) {
        state.orderCreationStep -= 1;
      }
    },
    
    resetOrderCreation: (state) => {
      state.orderCreationStep = 0;
      state.createdOrderId = null;
      state.error = null;
    },
    
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    clearFilters: (state) => {
      state.filters = {
        status: '',
        dateRange: null,
        sortBy: 'createdAt',
        sortDir: 'desc',
      };
    },
    
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    
    updateOrderInList: (state, action) => {
      const updatedOrder = action.payload;
      const index = state.orders.findIndex(order => order.id === updatedOrder.id);
      if (index !== -1) {
        state.orders[index] = updatedOrder;
      }
    },
    
    clearTrackingInfo: (state) => {
      state.trackingInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createdOrderId = action.payload.orderId || action.payload.id;
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      
      // Fetch User Orders
      .addCase(fetchUserOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.content || action.payload;
        state.currentPage = action.payload.number || 0;
        state.totalPages = action.payload.totalPages || 1;
        state.totalElements = action.payload.totalElements || state.orders.length;
        state.pageSize = action.payload.size || 10;
        state.error = null;
      })
      .addCase(fetchUserOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Order Status
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        
        // Update current order if it matches
        if (state.currentOrder && state.currentOrder.id === updatedOrder.id) {
          state.currentOrder = updatedOrder;
        }
        
        // Update order in list
        const index = state.orders.findIndex(order => order.id === updatedOrder.id);
        if (index !== -1) {
          state.orders[index] = updatedOrder;
        }
        
        state.error = null;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Cancel Order
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        const cancelledOrder = action.payload;
        
        // Update current order if it matches
        if (state.currentOrder && state.currentOrder.id === cancelledOrder.id) {
          state.currentOrder = cancelledOrder;
        }
        
        // Update order in list
        const index = state.orders.findIndex(order => order.id === cancelledOrder.id);
        if (index !== -1) {
          state.orders[index] = cancelledOrder;
        }
        
        state.error = null;
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Track Order
      .addCase(trackOrder.pending, (state) => {
        state.trackingLoading = true;
        state.error = null;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.trackingInfo = action.payload;
        state.error = null;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        state.trackingLoading = false;
        state.error = action.payload;
      })
      
      // Process Payment
      .addCase(processPayment.pending, (state) => {
        state.paymentLoading = true;
        state.error = null;
      })
      .addCase(processPayment.fulfilled, (state, action) => {
        state.paymentLoading = false;
        const updatedOrder = action.payload;
        
        if (state.currentOrder && state.currentOrder.id === updatedOrder.id) {
          state.currentOrder = updatedOrder;
        }
        
        state.error = null;
      })
      .addCase(processPayment.rejected, (state, action) => {
        state.paymentLoading = false;
        state.error = action.payload;
      })
      
      // Request Refund
      .addCase(requestRefund.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestRefund.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        
        if (state.currentOrder && state.currentOrder.id === updatedOrder.id) {
          state.currentOrder = updatedOrder;
        }
        
        const index = state.orders.findIndex(order => order.id === updatedOrder.id);
        if (index !== -1) {
          state.orders[index] = updatedOrder;
        }
        
        state.error = null;
      })
      .addCase(requestRefund.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Ship Order
      .addCase(shipOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(shipOrder.fulfilled, (state, action) => {
        state.loading = false;
        const shippedOrder = action.payload;
        
        // Update current order if it matches
        if (state.currentOrder && state.currentOrder.id === shippedOrder.id) {
          state.currentOrder = shippedOrder;
        }
        
        // Update order in list
        const index = state.orders.findIndex(order => order.id === shippedOrder.id);
        if (index !== -1) {
          state.orders[index] = shippedOrder;
        }
        
        state.error = null;
      })
      .addCase(shipOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Deliver Order
      .addCase(deliverOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deliverOrder.fulfilled, (state, action) => {
        state.loading = false;
        const deliveredOrder = action.payload;
        
        // Update current order if it matches
        if (state.currentOrder && state.currentOrder.id === deliveredOrder.id) {
          state.currentOrder = deliveredOrder;
        }
        
        // Update order in list
        const index = state.orders.findIndex(order => order.id === deliveredOrder.id);
        if (index !== -1) {
          state.orders[index] = deliveredOrder;
        }
        
        state.error = null;
      })
      .addCase(deliverOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add Order Note
      .addCase(addOrderNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOrderNote.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        
        if (state.currentOrder && state.currentOrder.id === updatedOrder.id) {
          state.currentOrder = updatedOrder;
        }
        
        state.error = null;
      })
      .addCase(addOrderNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentOrder,
  clearCurrentOrder,
  setOrderCreationStep,
  nextOrderCreationStep,
  previousOrderCreationStep,
  resetOrderCreation,
  setFilters,
  clearFilters,
  setCurrentPage,
  updateOrderInList,
  clearTrackingInfo,
} = orderSlice.actions;

export default orderSlice.reducer;

// Selectors
export const selectOrders = (state) => state.orders.orders;
export const selectCurrentOrder = (state) => state.orders.currentOrder;
export const selectOrderLoading = (state) => state.orders.loading;
export const selectOrderError = (state) => state.orders.error;
export const selectTrackingInfo = (state) => state.orders.trackingInfo;
export const selectCreatedOrderId = (state) => state.orders.createdOrderId;